import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { familyService } from '../services/familyService'
import { AddFamilyMember } from '../components/AddFamilyMember'
import './FamilyDetail.css'

export function FamilyDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: family, isLoading: familyLoading, error: familyError } = useQuery({
    queryKey: ['family', id],
    queryFn: () => familyService.getById(id!),
    enabled: !!id,
  })

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['family-members', id],
    queryFn: () => familyService.getMembers(id!),
    enabled: !!id,
  })

  const removeMemberMutation = useMutation({
    mutationFn: (patientId: string) => familyService.removeMember(id!, patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-members', id] })
      queryClient.invalidateQueries({ queryKey: ['family', id] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => familyService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] })
      navigate('/families')
    },
  })

  const handleRemoveMember = async (patientId: string, memberName: string) => {
    if (window.confirm(`Remove ${memberName} from this family?`)) {
      try {
        await removeMemberMutation.mutateAsync(patientId)
      } catch (error) {
        alert('Failed to remove family member')
      }
    }
  }

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete "${family?.name}"? This will unlink all family members but will not delete the patients.`
      )
    ) {
      try {
        await deleteMutation.mutateAsync()
      } catch (error) {
        alert('Failed to delete family')
      }
    }
  }

  if (familyLoading || membersLoading) return <div className="loading">Loading family details...</div>
  if (familyError) return <div className="error">Error loading family details</div>
  if (!family) return <div>Family not found</div>

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  return (
    <div className="family-detail">
      <div className="family-detail-header">
        <div>
          <h1>{family.name}</h1>
          {family.description && <p className="family-subtitle">{family.description}</p>}
        </div>
        <div className="family-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate(`/families/${id}/edit`)}
          >
            Edit Family
          </button>
          <button
            className="btn-danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Family'}
          </button>
        </div>
      </div>

      <div className="family-info-sections">
        <div className="info-section">
          <h2>Family Information</h2>
          <div className="info-grid">
            {family.primaryContactId && (
              <div className="info-item">
                <label>Primary Contact</label>
                <p>
                  {members?.find((m) => m.patientId === family.primaryContactId)?.firstName}{' '}
                  {members?.find((m) => m.patientId === family.primaryContactId)?.lastName}
                </p>
              </div>
            )}
            {family.phone && (
              <div className="info-item">
                <label>Phone</label>
                <p>{family.phone}</p>
              </div>
            )}
            {family.email && (
              <div className="info-item">
                <label>Email</label>
                <p>{family.email}</p>
              </div>
            )}
            {family.address && (
              <div className="info-item full-width">
                <label>Address</label>
                <p>
                  {[
                    family.address.street,
                    family.address.city,
                    family.address.state,
                    family.address.postalCode,
                    family.address.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
            )}
            <div className="info-item">
              <label>Status</label>
              <p>
                <span className={`badge ${family.isActive ? 'active' : 'inactive'}`}>
                  {family.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            {family.notes && (
              <div className="info-item full-width">
                <label>Notes</label>
                <p className="multiline">{family.notes}</p>
              </div>
            )}
          </div>
        </div>

        <div className="info-section">
          <div className="section-header">
            <h2>Family Members ({members?.length || 0})</h2>
            <AddFamilyMember familyId={id!} />
          </div>

          {!members || members.length === 0 ? (
            <div className="empty-members">
              <p>No family members yet. Add members to link patients to this family.</p>
            </div>
          ) : (
            <div className="members-list">
              {members.map((member) => (
                <div key={member.patientId} className="member-card">
                  <div className="member-info">
                    <div className="member-name">
                      <h3>
                        {member.firstName} {member.lastName}
                        {member.role === 'primary' && (
                          <span className="badge primary">Primary Contact</span>
                        )}
                        {member.role === 'dependent' && (
                          <span className="badge dependent">Dependent</span>
                        )}
                      </h3>
                      <p className="member-details">
                        {member.gender} • Age {calculateAge(member.dateOfBirth)} • DOB:{' '}
                        {formatDate(member.dateOfBirth)}
                      </p>
                    </div>
                    <div className="member-actions">
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/patients/${member.patientId}`)}
                      >
                        View Patient
                      </button>
                      <button
                        className="btn-link btn-danger"
                        onClick={() =>
                          handleRemoveMember(
                            member.patientId,
                            `${member.firstName} ${member.lastName}`
                          )
                        }
                        disabled={removeMemberMutation.isPending}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}



































